import { collection, query, where, getDocs, doc, getDoc, addDoc, Timestamp, writeBatch, updateDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { uploadProgramImage, uploadProgramVideo } from './storageService';

export const programService = {
  // 학생에게 배정된 프로그램 가져오기
  getStudentPrograms: async (studentId) => {
    try {
      const q = query(
        collection(db, 'student_programs'),
        where('studentId', '==', studentId)
      );
      const querySnapshot = await getDocs(q);

      const assignments = [];
      querySnapshot.forEach((doc) => {
        assignments.push({ id: doc.id, ...doc.data() });
      });

      // 각 배정에 대한 프로그램 정보 가져오기
      const programsData = await Promise.all(
        assignments.map(async (assignment) => {
          const programDoc = await getDoc(doc(db, 'programs', assignment.programId));
          if (programDoc.exists()) {
            return {
              assignmentId: assignment.id,
              assignment: assignment,
              program: { id: programDoc.id, ...programDoc.data() }
            };
          }
          return null;
        })
      );

      return programsData.filter(p => p !== null);
    } catch (error) {
      console.error('Error fetching student programs:', error);
      throw new Error('프로그램 정보를 불러오는데 실패했습니다.');
    }
  },

  // 프로그램 ID로 프로그램 정보 가져오기
  getProgramById: async (programId) => {
    try {
      const programDoc = await getDoc(doc(db, 'programs', programId));
      if (programDoc.exists()) {
        return { id: programDoc.id, ...programDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching program:', error);
      throw new Error('프로그램 정보를 불러오는데 실패했습니다.');
    }
  },

  // 프로그램의 세션 기록 가져오기
  getProgramSessions: async (studentId, programId) => {
    try {
      const q = query(
        collection(db, 'sessions'),
        where('studentId', '==', studentId),
        where('programId', '==', programId)
      );
      const querySnapshot = await getDocs(q);

      const sessions = [];

      // 각 세션에 대해 실제 trials 기반으로 카운트 계산
      for (const docSnapshot of querySnapshot.docs) {
        const sessionData = { id: docSnapshot.id, ...docSnapshot.data() };

        // 해당 세션의 trials 가져오기
        const trialsQuery = query(
          collection(db, 'trials'),
          where('sessionId', '==', docSnapshot.id)
        );
        const trialsSnapshot = await getDocs(trialsQuery);

        // trials가 있으면 실제 데이터로 카운트 계산
        if (!trialsSnapshot.empty) {
          let successCount = 0;
          let failureCount = 0;
          let helpedSuccessCount = 0;
          let notPerformedCount = 0;

          trialsSnapshot.forEach((trialDoc) => {
            const trial = trialDoc.data();
            switch (trial.status) {
              case 'success':
                successCount++;
                break;
              case 'failure':
                failureCount++;
                break;
              case 'helped_success':
                helpedSuccessCount++;
                break;
              case 'not_performed':
                notPerformedCount++;
                break;
            }
          });

          // 실제 trials 데이터로 덮어쓰기
          sessionData.successCount = successCount;
          sessionData.failureCount = failureCount;
          sessionData.helpedSuccessCount = helpedSuccessCount;
          sessionData.notPerformedCount = notPerformedCount;
          sessionData.repetitionCount = trialsSnapshot.size;
        }

        sessions.push(sessionData);
      }

      // 날짜순으로 정렬 (최신순)
      sessions.sort((a, b) => {
        const dateA = a.sessionDate?.toDate ? a.sessionDate.toDate() : new Date(a.sessionDate);
        const dateB = b.sessionDate?.toDate ? b.sessionDate.toDate() : new Date(b.sessionDate);
        return dateB - dateA;
      });

      return sessions;
    } catch (error) {
      console.error('Error fetching program sessions:', error);
      throw new Error('세션 기록을 불러오는데 실패했습니다.');
    }
  },

  // 세션의 시도 기록 가져오기
  getSessionTrials: async (sessionId) => {
    try {
      const q = query(
        collection(db, 'trials'),
        where('sessionId', '==', sessionId)
      );
      const querySnapshot = await getDocs(q);

      const trials = [];
      querySnapshot.forEach((doc) => {
        trials.push({ id: doc.id, ...doc.data() });
      });

      // 시도 번호순으로 정렬
      trials.sort((a, b) => (a.trialNumber || 0) - (b.trialNumber || 0));

      return trials;
    } catch (error) {
      console.error('Error fetching session trials:', error);
      throw new Error('시도 기록을 불러오는데 실패했습니다.');
    }
  },

  // 대주제 목록 가져오기 (학생의 프로그램 중에서)
  getMajorCategories: (programs) => {
    const categories = [...new Set(programs.map(p => p.program.majorCategory))];
    return categories.sort();
  },

  // 중주제 목록 가져오기 (선택된 대주제에서)
  getMiddleCategories: (programs, majorCategory) => {
    const filtered = programs.filter(p => p.program.majorCategory === majorCategory);
    const categories = [...new Set(filtered.map(p => p.program.middleCategory))];
    return categories.sort();
  },

  // 프로그램 목록 가져오기 (선택된 대주제와 중주제에서)
  getPrograms: (programs, majorCategory, middleCategory) => {
    return programs.filter(p =>
      p.program.majorCategory === majorCategory &&
      p.program.middleCategory === middleCategory
    );
  },

  // 새 프로그램 추가 및 학생에게 배정
  addProgramToStudent: async (studentId, programData, options = {}) => {
    try {
      const { createTemplate = true } = options;
      const { referenceFiles, ...programDataWithoutFiles } = programData;

      let templateId = null;

      // 1. 새 프로그램인 경우 템플릿도 생성
      if (createTemplate) {
        templateId = await programService.createProgramTemplate(programData);
      }

      // 2. programs 컬렉션에 추가하여 programId 생성
      const programRef = await addDoc(collection(db, 'programs'), {
        ...programDataWithoutFiles,
        templateId: templateId, // 템플릿 ID 기록
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // 3. 파일 업로드가 있는 경우 처리
      let uploadedImages = [];
      let uploadedVideos = [];

      if (referenceFiles && (referenceFiles.images?.length > 0 || referenceFiles.videos?.length > 0)) {
        // 이미지 업로드
        if (referenceFiles.images?.length > 0) {
          const imageUploadPromises = referenceFiles.images.map((image, index) =>
            uploadProgramImage(image, programRef.id, index)
          );
          uploadedImages = await Promise.all(imageUploadPromises);
        }

        // 동영상 업로드
        if (referenceFiles.videos?.length > 0) {
          const videoUploadPromises = referenceFiles.videos.map((video, index) =>
            uploadProgramVideo(video, programRef.id, index)
          );
          uploadedVideos = await Promise.all(videoUploadPromises);
        }

        // 업로드된 파일 URL로 프로그램 문서 업데이트
        const programDocRef = doc(db, 'programs', programRef.id);
        await updateDoc(programDocRef, {
          referenceFiles: {
            images: uploadedImages,
            videos: uploadedVideos
          },
          updatedAt: Timestamp.now()
        });
      }

      // 4. student_programs 컬렉션에 배정 정보 추가
      const now = Timestamp.now();
      await addDoc(collection(db, 'student_programs'), {
        studentId,
        programId: programRef.id,
        assignedAt: now,
        createdAt: now,
        updatedAt: now
      });

      return programRef.id;
    } catch (error) {
      console.error('Error adding program:', error);
      throw new Error('프로그램 추가에 실패했습니다: ' + error.message);
    }
  },

  /**
   * Update program data
   * @param {string} programId - Program document ID
   * @param {Object} programData - Program data to update
   */
  updateProgram: async (programId, programData) => {
    try {
      const { referenceFiles, ...programDataWithoutFiles } = programData;
      const programRef = doc(db, 'programs', programId);

      // 파일 업로드가 있는 경우 처리
      let uploadedImages = programData.referenceFiles?.images || [];
      let uploadedVideos = programData.referenceFiles?.videos || [];

      // 새로운 파일이 File 객체인 경우에만 업로드
      if (referenceFiles) {
        if (referenceFiles.images?.length > 0) {
          const newImages = referenceFiles.images.filter(img => img instanceof File);
          const existingImages = referenceFiles.images.filter(img => !(img instanceof File));

          if (newImages.length > 0) {
            const imageUploadPromises = newImages.map((image, index) =>
              uploadProgramImage(image, programId, existingImages.length + index)
            );
            const newUploadedImages = await Promise.all(imageUploadPromises);
            uploadedImages = [...existingImages, ...newUploadedImages];
          } else {
            uploadedImages = existingImages;
          }
        }

        if (referenceFiles.videos?.length > 0) {
          const newVideos = referenceFiles.videos.filter(vid => vid instanceof File);
          const existingVideos = referenceFiles.videos.filter(vid => !(vid instanceof File));

          if (newVideos.length > 0) {
            const videoUploadPromises = newVideos.map((video, index) =>
              uploadProgramVideo(video, programId, existingVideos.length + index)
            );
            const newUploadedVideos = await Promise.all(videoUploadPromises);
            uploadedVideos = [...existingVideos, ...newUploadedVideos];
          } else {
            uploadedVideos = existingVideos;
          }
        }
      }

      await updateDoc(programRef, {
        ...programDataWithoutFiles,
        referenceFiles: {
          images: uploadedImages,
          videos: uploadedVideos
        },
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating program:', error);
      throw new Error('프로그램 수정에 실패했습니다: ' + error.message);
    }
  },

  /**
   * Delete program and its assignments
   * @param {string} programId - Program document ID
   * @param {string} assignmentId - Assignment document ID (optional, for specific assignment)
   */
  deleteProgram: async (programId, assignmentId) => {
    try {
      const batch = writeBatch(db);

      // Delete Program document
      const programRef = doc(db, 'programs', programId);
      batch.delete(programRef);

      // Delete Assignment document if provided
      if (assignmentId) {
        const assignmentRef = doc(db, 'student_programs', assignmentId);
        batch.delete(assignmentRef);
      } else {
        // Delete all assignments for this program
        const assignmentsQuery = query(
          collection(db, 'student_programs'),
          where('programId', '==', programId)
        );
        const assignmentsSnapshot = await getDocs(assignmentsQuery);
        assignmentsSnapshot.forEach((docSnapshot) => {
          batch.delete(docSnapshot.ref);
        });
      }

      // Delete all sessions for this program
      const sessionsQuery = query(
        collection(db, 'sessions'),
        where('programId', '==', programId)
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      // Delete all trials for each session
      for (const sessionDoc of sessionsSnapshot.docs) {
        const trialsQuery = query(
          collection(db, 'trials'),
          where('sessionId', '==', sessionDoc.id)
        );
        const trialsSnapshot = await getDocs(trialsQuery);
        trialsSnapshot.forEach((trialDoc) => {
          batch.delete(trialDoc.ref);
        });
        batch.delete(sessionDoc.ref);
      }

      await batch.commit();
    } catch (error) {
      console.error('Error deleting program:', error);
      throw new Error('프로그램 삭제에 실패했습니다.');
    }
  },

  /**
   * Update program status in both Program and Assignment documents
   * @param {string} programId - Program document ID
   * @param {string} assignmentId - Assignment document ID
   * @param {string} newStatus - New status ('active', 'completed', 'paused')
   */
  updateProgramStatus: async (programId, assignmentId, newStatus) => {
    try {
      const batch = writeBatch(db);

      // Update Program document
      const programRef = doc(db, 'programs', programId);
      batch.update(programRef, {
        status: newStatus,
        updatedAt: Timestamp.now()
      });

      // Update Assignment document (student_programs collection)
      const assignmentRef = doc(db, 'student_programs', assignmentId);
      batch.update(assignmentRef, {
        status: newStatus,
        updatedAt: Timestamp.now()
      });

      await batch.commit();
    } catch (error) {
      console.error('Error updating program status:', error);
      throw new Error('프로그램 상태 업데이트에 실패했습니다.');
    }
  },

  /**
   * Check if a session meets success criteria based on behavior type
   * @param {Object} sessionData - Session data
   * @param {number} successCriteria - Success count threshold
   * @param {string} behaviorType - 'positive_above' or 'perform_only'
   * @returns {boolean} - True if session meets criteria
   */
  isSessionSuccessful: (sessionData, successCriteria, behaviorType) => {
    if (behaviorType === 'positive_above') {
      // Positive behavior: successCount must be >= successCriteria
      const successCount = sessionData.successCount || 0;
      return successCount >= successCriteria;
    } else if (behaviorType === 'perform_only') {
      // Perform only: performedCount must be >= successCriteria
      const performedCount = sessionData.performedCount || sessionData.successCount || 0;
      return performedCount >= successCriteria;
    }
    return false;
  },

  /**
   * Check if program meets completion criteria
   * @param {string} assignmentId - Assignment document ID
   * @param {number} successCriteria - Session success count threshold
   * @param {string} behaviorType - 'positive_above' or 'perform_only'
   * @param {number} programSuccessCriteria - Number of sessions needed
   * @param {string} programSuccessMode - 'consecutive' or 'cumulative'
   * @returns {boolean} - True if program should be completed
   */
  checkProgramCompletion: async (assignmentId, successCriteria, behaviorType, programSuccessCriteria, programSuccessMode) => {
    try {
      console.log('[checkProgramCompletion] Starting check for assignmentId:', assignmentId);
      console.log('[checkProgramCompletion] Session success criteria:', successCriteria);
      console.log('[checkProgramCompletion] Behavior type:', behaviorType);
      console.log('[checkProgramCompletion] Program success criteria:', programSuccessCriteria);
      console.log('[checkProgramCompletion] Program success mode:', programSuccessMode);

      // Get all sessions for this assignment, ordered by date (newest first)
      const q = query(
        collection(db, 'sessions'),
        where('assignmentId', '==', assignmentId),
        orderBy('sessionDate', 'desc')
      );
      const querySnapshot = await getDocs(q);

      console.log('[checkProgramCompletion] Total sessions found:', querySnapshot.size);

      if (querySnapshot.size === 0) {
        console.log('[checkProgramCompletion] No sessions found');
        return false;
      }

      const sessions = [];
      querySnapshot.forEach((docSnapshot) => {
        sessions.push(docSnapshot.data());
      });

      if (programSuccessMode === 'consecutive') {
        // Consecutive mode: Check last N sessions
        console.log('[checkProgramCompletion] Checking consecutive mode');

        if (sessions.length < programSuccessCriteria) {
          console.log('[checkProgramCompletion] Not enough sessions (need', programSuccessCriteria, 'have', sessions.length, ')');
          return false;
        }

        // Check last N sessions
        const recentSessions = sessions.slice(0, programSuccessCriteria);
        const allSuccessful = recentSessions.every(session =>
          programService.isSessionSuccessful(session, successCriteria, behaviorType)
        );

        console.log('[checkProgramCompletion] All', programSuccessCriteria, 'consecutive sessions successful:', allSuccessful);
        return allSuccessful;

      } else if (programSuccessMode === 'cumulative') {
        // Cumulative mode: Count total successful sessions
        console.log('[checkProgramCompletion] Checking cumulative mode');

        const successfulSessionCount = sessions.filter(session =>
          programService.isSessionSuccessful(session, successCriteria, behaviorType)
        ).length;

        console.log('[checkProgramCompletion] Successful sessions:', successfulSessionCount, '/', programSuccessCriteria);
        return successfulSessionCount >= programSuccessCriteria;
      }

      return false;
    } catch (error) {
      console.error('Error checking program completion:', error);
      return false;
    }
  },

  /**
   * Check and auto-complete program based on configured success criteria
   * @param {string} programId - Program document ID
   * @param {string} assignmentId - Assignment document ID
   */
  checkAndAutoComplete: async (programId, assignmentId) => {
    try {
      console.log('[checkAndAutoComplete] Starting check for programId:', programId, 'assignmentId:', assignmentId);

      // Get program to check success criteria
      const programDoc = await getDoc(doc(db, 'programs', programId));
      if (!programDoc.exists()) {
        console.log('[checkAndAutoComplete] Program document not found');
        return;
      }

      const programData = programDoc.data();
      const successCriteria = programData.successCriteria || 0;
      const behaviorType = programData.behaviorType || 'positive_above';
      const programSuccessCriteria = programData.programSuccessCriteria || 5;
      const programSuccessMode = programData.programSuccessMode || 'consecutive';

      console.log('[checkAndAutoComplete] Program configuration:', {
        successCriteria,
        behaviorType,
        programSuccessCriteria,
        programSuccessMode
      });

      // Check if program meets completion criteria
      const shouldComplete = await programService.checkProgramCompletion(
        assignmentId,
        successCriteria,
        behaviorType,
        programSuccessCriteria,
        programSuccessMode
      );

      console.log('[checkAndAutoComplete] Should complete:', shouldComplete);

      // Auto-complete if criteria met
      if (shouldComplete) {
        console.log('[checkAndAutoComplete] AUTO-COMPLETING program');
        await programService.updateProgramStatus(programId, assignmentId, 'completed');
        console.log('[checkAndAutoComplete] Program status updated to completed');
      }
    } catch (error) {
      console.error('Error in auto-complete check:', error);
    }
  },

  /**
   * Check if a program can create new sessions
   * @param {string} assignmentId - Assignment document ID
   * @returns {Object} - {canCreate: boolean, reason?: string}
   */
  canCreateSession: async (assignmentId) => {
    try {
      const assignmentDoc = await getDoc(doc(db, 'student_programs', assignmentId));

      if (!assignmentDoc.exists()) {
        return { canCreate: false, reason: '프로그램을 찾을 수 없습니다.' };
      }

      const assignment = assignmentDoc.data();
      const status = assignment.status;

      if (status === 'completed') {
        return { canCreate: false, reason: '완료된 프로그램입니다.' };
      }

      if (status === 'paused') {
        return { canCreate: false, reason: '중지된 프로그램입니다.' };
      }

      return { canCreate: true };
    } catch (error) {
      console.error('Error checking session creation:', error);
      return { canCreate: false, reason: '상태 확인에 실패했습니다.' };
    }
  },

  // 전체 데이터베이스에서 모든 프로그램 가져오기 (자동완성용)
  getAllPrograms: async () => {
    try {
      const q = query(collection(db, 'programs'));
      const querySnapshot = await getDocs(q);

      const programs = [];
      querySnapshot.forEach((doc) => {
        programs.push({ id: doc.id, ...doc.data() });
      });

      return programs;
    } catch (error) {
      console.error('Error fetching all programs:', error);
      throw new Error('전체 프로그램 목록을 불러오는데 실패했습니다.');
    }
  },

  /**
   * 프로그램 템플릿 관련 함수들
   */

  // 모든 프로그램 템플릿 가져오기
  getAllProgramTemplates: async () => {
    try {
      const q = query(
        collection(db, 'program_templates'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const templates = [];
      querySnapshot.forEach((doc) => {
        templates.push({ id: doc.id, ...doc.data() });
      });

      return templates;
    } catch (error) {
      console.error('Error fetching program templates:', error);
      throw new Error('프로그램 템플릿 목록을 불러오는데 실패했습니다.');
    }
  },

  // 프로그램 템플릿 생성
  createProgramTemplate: async (programData) => {
    try {
      const { referenceFiles, ...programDataWithoutFiles } = programData;

      // 템플릿 생성
      const templateRef = await addDoc(collection(db, 'program_templates'), {
        ...programDataWithoutFiles,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // 파일 업로드가 있는 경우 처리
      let uploadedImages = [];
      let uploadedVideos = [];

      if (referenceFiles && (referenceFiles.images?.length > 0 || referenceFiles.videos?.length > 0)) {
        // 이미지 업로드
        if (referenceFiles.images?.length > 0) {
          const imageUploadPromises = referenceFiles.images.map((image, index) =>
            uploadProgramImage(image, templateRef.id, index)
          );
          uploadedImages = await Promise.all(imageUploadPromises);
        }

        // 동영상 업로드
        if (referenceFiles.videos?.length > 0) {
          const videoUploadPromises = referenceFiles.videos.map((video, index) =>
            uploadProgramVideo(video, templateRef.id, index)
          );
          uploadedVideos = await Promise.all(videoUploadPromises);
        }

        // 업로드된 파일 URL로 템플릿 업데이트
        const templateDocRef = doc(db, 'program_templates', templateRef.id);
        await updateDoc(templateDocRef, {
          referenceFiles: {
            images: uploadedImages,
            videos: uploadedVideos
          },
          updatedAt: Timestamp.now()
        });
      }

      return templateRef.id;
    } catch (error) {
      console.error('Error creating program template:', error);
      throw new Error('프로그램 템플릿 생성에 실패했습니다: ' + error.message);
    }
  },

  // 프로그램 템플릿 수정
  updateProgramTemplate: async (templateId, programData) => {
    try {
      const { referenceFiles, ...programDataWithoutFiles } = programData;
      const templateRef = doc(db, 'program_templates', templateId);

      // 파일 업로드가 있는 경우 처리
      let uploadedImages = programData.referenceFiles?.images || [];
      let uploadedVideos = programData.referenceFiles?.videos || [];

      // 새로운 파일이 File 객체인 경우에만 업로드
      if (referenceFiles) {
        if (referenceFiles.images?.length > 0) {
          const newImages = referenceFiles.images.filter(img => img instanceof File);
          const existingImages = referenceFiles.images.filter(img => !(img instanceof File));

          if (newImages.length > 0) {
            const imageUploadPromises = newImages.map((image, index) =>
              uploadProgramImage(image, templateId, existingImages.length + index)
            );
            const newUploadedImages = await Promise.all(imageUploadPromises);
            uploadedImages = [...existingImages, ...newUploadedImages];
          } else {
            uploadedImages = existingImages;
          }
        }

        if (referenceFiles.videos?.length > 0) {
          const newVideos = referenceFiles.videos.filter(vid => vid instanceof File);
          const existingVideos = referenceFiles.videos.filter(vid => !(vid instanceof File));

          if (newVideos.length > 0) {
            const videoUploadPromises = newVideos.map((video, index) =>
              uploadProgramVideo(video, templateId, existingVideos.length + index)
            );
            const newUploadedVideos = await Promise.all(videoUploadPromises);
            uploadedVideos = [...existingVideos, ...newUploadedVideos];
          } else {
            uploadedVideos = existingVideos;
          }
        }
      }

      await updateDoc(templateRef, {
        ...programDataWithoutFiles,
        referenceFiles: {
          images: uploadedImages,
          videos: uploadedVideos
        },
        updatedAt: Timestamp.now()
      });

      return templateId;
    } catch (error) {
      console.error('Error updating program template:', error);
      throw new Error('프로그램 템플릿 수정에 실패했습니다: ' + error.message);
    }
  },

  // 템플릿으로부터 프로그램 생성 (학생에게 배정)
  createProgramFromTemplate: async (studentId, templateId) => {
    try {
      // 템플릿 데이터 가져오기
      const templateDoc = await getDoc(doc(db, 'program_templates', templateId));
      if (!templateDoc.exists()) {
        throw new Error('템플릿을 찾을 수 없습니다.');
      }

      const templateData = templateDoc.data();

      // programs 컬렉션에 복사본 생성
      const programRef = await addDoc(collection(db, 'programs'), {
        ...templateData,
        templateId: templateId, // 어떤 템플릿으로부터 생성되었는지 기록
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // student_programs 컬렉션에 배정 정보 추가
      const now = Timestamp.now();
      await addDoc(collection(db, 'student_programs'), {
        studentId,
        programId: programRef.id,
        assignedAt: now,
        createdAt: now,
        updatedAt: now
      });

      return programRef.id;
    } catch (error) {
      console.error('Error creating program from template:', error);
      throw new Error('템플릿으로부터 프로그램 생성에 실패했습니다: ' + error.message);
    }
  }
};
